import { useState, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Avatar, Badge, Button, Card, Skeleton, StatusMessage } from '@aimsos/ui';
import { AlertTriangle, Calendar, MapPin, Pencil, Sparkles, Timer, Users, Zap } from 'lucide-react';

import { EditWorkspaceModal, WorkspaceOverviewStatCard } from '../components';
import { displayInitials, formatOverviewCreatedAt } from '../utils';

/**
 * Overview tab — data from parent GET /v1/workspace/:id (see WorkspaceDetailPage Outlet).
 * Route: /workspaces/:workspaceId/overview
 */
const WorkspaceOverviewPage = () => {
  const { workspaceId } = useParams();
  const { t, i18n } = useTranslation('workspaces');
  const outlet = useOutletContext();
  const wd = outlet?.workspaceDetail;

  const overview = wd?.data ?? null;
  const loading = Boolean(wd?.loading) && !overview;
  const error = wd?.error ?? null;

  const [editOpen, setEditOpen] = useState(false);

  const handleEditWorkspace = useCallback(() => {
    setEditOpen(true);
  }, []);

  if (loading && !overview) {
    return (
      <div className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4">
        
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <Skeleton height="20px" width="180px" borderRadius="6px" />
          <Skeleton height="36px" width="140px" borderRadius="8px" />
        </div>

        {/* Card */}
        <Card className="border-edge p-6">
          <div className="grid grid-cols-[auto_1fr] gap-6 items-center">
            
            {/* Avatar */}
            <Skeleton height="80px" width="80px" borderRadius="9999px" />

            {/* Content */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4">
              
              {/* Row 1 */}
              <Skeleton height="40px" width="140px" borderRadius="6px" />
              <Skeleton height="40px" width="180px" borderRadius="6px" />
              <Skeleton height="40px" width="120px" borderRadius="6px" />

              {/* Row 2 */}
              <Skeleton height="32px" width="80px" borderRadius="9999px" />
              <Skeleton
                height="40px"
                width="100%"
                borderRadius="6px"
                className="col-span-2"
              />
            </div>
          </div>
        </Card>

        {/* Bottom section */}
        <div>
          <Skeleton height="20px" width="200px" borderRadius="6px" className="mb-2" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="120px" width="100%" borderRadius="8px" />
            ))}
          </div>
        </div>

      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4">
        <StatusMessage icon={AlertTriangle} title={t('detail.overview.loadError')} subtitle={error} />
      </div>
    );
  }

  const o = overview;
  const name = o?.workspaceName ?? '';
  const initials = displayInitials(name);
  const ownerInitials = displayInitials(o?.ownerName ?? '');
  const createdLabel = formatOverviewCreatedAt(o?.createdAt, i18n.language);
  const hasPhoto = Boolean(o?.photoUrl?.trim());

  return (
    <div className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4">
      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-content">{t('detail.overview.workspaceInfo')}</h2>
          <Button type="button" variant="secondary" size="sm" leftIcon={Pencil} onClick={handleEditWorkspace}>
            {t('detail.overview.editWorkspace')}
          </Button>
        </div>
      <Card className="border-edge p-6 md:p-8">
        <div className="grid grid-cols-[auto_1fr] gap-6 items-center">
          
          {/* Avatar */}
          <div>
            <Avatar
              src={hasPhoto ? o.photoUrl : undefined}
              initials={hasPhoto ? undefined : initials}
              alt={name || t('common.workspace')}
              size="xxl"
              className={
                hasPhoto
                  ? '!h-20 !w-20'
                  : '!h-20 !w-20 border-red-700 bg-red-600 text-lg text-white'
              }
            />
          </div>

          {/* Content */}
          <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
            
            {/* Row 1 */}
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                {t('detail.overview.fields.name')}
              </p>
              <p className="text-base font-semibold text-content">
                {name || '—'}
              </p>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                {t('detail.overview.fields.owner')}
              </p>
              {o?.ownerName ? (
                <div className="flex items-center gap-2">
                  <Avatar
                    initials={ownerInitials}
                    alt={o.ownerName}
                    size="md"
                    className="!h-8 !w-8 border-orange-600 bg-orange-500 text-xs text-white"
                  />
                  <p className="text-sm text-content truncate">
                    {o.ownerName}
                  </p>
                </div>
              ) : (
                <p className="text-sm italic text-content-faint">
                  {t('detail.overview.noOwner')}
                </p>
              )}
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                {t('detail.overview.fields.createdOn')}
              </p>
              <div className="flex items-center gap-2 text-sm text-content">
                <Calendar size={16} className="text-content-muted" />
                {createdLabel}
              </div>
            </div>

            {/* Row 2 */}
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                {t('detail.overview.fields.status')}
              </p>
              <Badge
                variant={o?.isActive ? 'success' : 'neutral'}
                size="sm"
                className="rounded-full w-fit"
              >
                {o?.isActive
                  ? t('list.card.statusActive')
                  : t('detail.overview.inactiveStatus')}
              </Badge>
            </div>

            <div className="col-span-2">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                {t('detail.overview.fields.location')}
              </p>
              <div className="flex items-start gap-2 text-sm text-content">
                <MapPin size={16} className="mt-0.5 text-content-muted" />
                <span className="truncate">
                  {o?.address?.trim() ? o.address : '—'}
                </span>
              </div>
            </div>

          </div>
        </div>
      </Card>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-content">{t('detail.overview.workspaceStatistics')}</h2>
        <div className="flex flex-row gap-4">
          <WorkspaceOverviewStatCard
            label={t('detail.overview.stats.activeUsers')}
            value={o?.userActiveCount ?? 0}
            icon={Users}
            iconClassName="bg-teal-500/15 text-teal-600 dark:text-teal-400"
          />
          <WorkspaceOverviewStatCard
            label={t('detail.overview.stats.totalAgents')}
            value={o?.agents ?? 0}
            icon={Sparkles}
            iconClassName="bg-teal-500/15 text-teal-600 dark:text-teal-400"
          />
          <WorkspaceOverviewStatCard
            label={t('detail.overview.stats.activeWorkflows')}
            value={o?.workflows ?? 0}
            hint={t('detail.overview.stats.workflowsHint')}
            icon={Zap}
            iconClassName="bg-teal-500/15 text-teal-600 dark:text-teal-400"
          />
          <WorkspaceOverviewStatCard
            label={t('detail.overview.stats.activeCampaigns')}
            value={o?.campaigns ?? 0}
            hint={t('detail.overview.stats.campaignsHint')}
            icon={Timer}
            iconClassName="bg-surface-elevated text-content-muted dark:bg-surface-muted"
          />
        </div>
      </section>

      {workspaceId && o ? (
        <EditWorkspaceModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          workspaceId={workspaceId}
          customerSlug={
            o.slug?.trim() ||
            (o.workspaceName?.trim()
              ? o.workspaceName.trim().toLowerCase().replace(/\s+/g, '-')
              : '')
          }
          initialName={o.workspaceName}
          initialContactEmail={o.ownerEmail ?? ''}
          initialContactName={o.ownerName ?? ''}
          initialAddress={o.address ?? ''}
          initialPhone={o.phoneNumber ?? ''}
          initialPhotoUrl={o.photoUrl}
          onUpdated={() => wd?.refresh?.()}
        />
      ) : null}
    </div>
  );
};

export default WorkspaceOverviewPage;
